// Achievement system types and definitions for Clevia

export type AchievementRarity = 'basic' | 'important' | 'advanced' | 'milestone';

export type AchievementStatus = 'locked' | 'in_progress' | 'unlocked' | 'newly_unlocked';

export type AchievementCategory =
  | 'first_steps'
  | 'budget_organization'
  | 'regularity'
  | 'financial_control'
  | 'product_exploration';

export interface AchievementDefinition {
  key: string;
  category: AchievementCategory;
  rarity: AchievementRarity;
  icon: string; // emoji
  nameEn: string;
  nameFr: string;
  descriptionEn: string;
  descriptionFr: string;
  // Progress tracking (optional — for achievements that track counts)
  targetCount?: number;
}

export interface UserAchievement {
  id: string;
  user_id: string;
  achievement_key: string;
  unlocked_at: string;
  seen: boolean;
  created_at: string;
}

export interface AchievementWithStatus extends AchievementDefinition {
  status: AchievementStatus;
  unlockedAt?: string;
  progress?: number; // 0-1 for in_progress
}

// ─── Achievement Definitions (MVP) ───────────────────────────────────

export const ACHIEVEMENT_CATEGORIES: Record<
  AchievementCategory,
  { labelEn: string; labelFr: string; icon: string }
> = {
  first_steps: { labelEn: 'First Steps', labelFr: 'Premiers pas', icon: '🚀' },
  budget_organization: {
    labelEn: 'Budget Organization',
    labelFr: 'Organisation budgétaire',
    icon: '📊',
  },
  regularity: { labelEn: 'Regularity', labelFr: 'Régularité', icon: '🔄' },
  financial_control: {
    labelEn: 'Financial Control',
    labelFr: 'Contrôle financier',
    icon: '🎯',
  },
  product_exploration: {
    labelEn: 'Product Exploration',
    labelFr: 'Exploration produit',
    icon: '🔍',
  },
};

export const ACHIEVEMENTS: AchievementDefinition[] = [
  // ── Category 1: First Steps ───────────────────────────────────
  {
    key: 'first_login',
    category: 'first_steps',
    rarity: 'basic',
    icon: '👋',
    nameEn: 'Welcome!',
    nameFr: 'Bienvenue !',
    descriptionEn: 'Log in for the first time',
    descriptionFr: 'Connectez-vous pour la première fois',
  },
  {
    key: 'profile_completed',
    category: 'first_steps',
    rarity: 'basic',
    icon: '✓',
    nameEn: 'Profile Completed',
    nameFr: 'Profil complété',
    descriptionEn: 'Complete your profile information',
    descriptionFr: 'Complétez vos informations de profil',
  },
  {
    key: 'first_account',
    category: 'first_steps',
    rarity: 'basic',
    icon: '🏦',
    nameEn: 'First Account',
    nameFr: 'Premier compte',
    descriptionEn: 'Add your first account',
    descriptionFr: 'Ajoutez votre premier compte',
  },
  {
    key: 'first_category',
    category: 'first_steps',
    rarity: 'basic',
    icon: '🏷️',
    nameEn: 'Organized',
    nameFr: 'Organisé',
    descriptionEn: 'Create or customize a category',
    descriptionFr: 'Créez ou personnalisez une catégorie',
  },
  {
    key: 'first_transaction',
    category: 'first_steps',
    rarity: 'basic',
    icon: '💳',
    nameEn: 'First Transaction',
    nameFr: 'Première transaction',
    descriptionEn: 'Add your first transaction',
    descriptionFr: 'Ajoutez votre première transaction',
  },
  {
    key: 'first_income',
    category: 'first_steps',
    rarity: 'basic',
    icon: '💰',
    nameEn: 'First Income',
    nameFr: 'Premier revenu',
    descriptionEn: 'Add your first income',
    descriptionFr: 'Ajoutez votre premier revenu',
  },
  {
    key: 'first_expense',
    category: 'first_steps',
    rarity: 'basic',
    icon: '🛒',
    nameEn: 'First Expense',
    nameFr: 'Première dépense',
    descriptionEn: 'Add your first expense',
    descriptionFr: 'Ajoutez votre première dépense',
  },
  {
    key: 'first_transfer',
    category: 'first_steps',
    rarity: 'basic',
    icon: '🔀',
    nameEn: 'First Transfer',
    nameFr: 'Premier virement',
    descriptionEn: 'Make your first transfer between accounts',
    descriptionFr: 'Effectuez votre premier virement entre comptes',
  },

  // ── Category 2: Budget Organization ─────────────────────────────
  {
    key: 'first_goal',
    category: 'budget_organization',
    rarity: 'important',
    icon: '🎯',
    nameEn: 'First Goal',
    nameFr: 'Premier objectif',
    descriptionEn: 'Create your first savings goal',
    descriptionFr: "Créez votre premier objectif d'épargne",
  },
  {
    key: 'first_goal_deposit',
    category: 'budget_organization',
    rarity: 'important',
    icon: '💎',
    nameEn: 'First Deposit',
    nameFr: 'Premier dépôt',
    descriptionEn: 'Make your first deposit towards a goal',
    descriptionFr: 'Effectuez votre premier dépôt vers un objectif',
  },
  {
    key: 'first_recurring',
    category: 'budget_organization',
    rarity: 'important',
    icon: '🔁',
    nameEn: 'Recurring Setup',
    nameFr: 'Récurrence créée',
    descriptionEn: 'Create your first recurring transaction',
    descriptionFr: 'Créez votre première transaction récurrente',
  },
  {
    key: 'structured_budget',
    category: 'budget_organization',
    rarity: 'advanced',
    icon: '📋',
    nameEn: 'Structured Budget',
    nameFr: 'Budget structuré',
    descriptionEn: 'Have at least 5 transactions and 2 accounts',
    descriptionFr: 'Ayez au moins 5 transactions et 2 comptes',
  },
  {
    key: 'complete_view',
    category: 'budget_organization',
    rarity: 'important',
    icon: '👁️',
    nameEn: 'Complete Overview',
    nameFr: 'Vue complète',
    descriptionEn: 'Have at least one account, one transaction, and one category',
    descriptionFr: 'Ayez au moins un compte, une transaction et une catégorie utilisée',
  },

  // ── Category 3: Regularity ────────────────────────────────────
  {
    key: 'transactions_3',
    category: 'regularity',
    rarity: 'basic',
    icon: '📝',
    nameEn: '3 Transactions',
    nameFr: '3 transactions',
    descriptionEn: 'Add 3 transactions',
    descriptionFr: 'Ajoutez 3 transactions',
    targetCount: 3,
  },
  {
    key: 'transactions_10',
    category: 'regularity',
    rarity: 'basic',
    icon: '📊',
    nameEn: '10 Transactions',
    nameFr: '10 transactions',
    descriptionEn: 'Add 10 transactions',
    descriptionFr: 'Ajoutez 10 transactions',
    targetCount: 10,
  },
  {
    key: 'transactions_25',
    category: 'regularity',
    rarity: 'important',
    icon: '📈',
    nameEn: '25 Transactions',
    nameFr: '25 transactions',
    descriptionEn: 'Add 25 transactions',
    descriptionFr: 'Ajoutez 25 transactions',
    targetCount: 25,
  },
  {
    key: 'transactions_50',
    category: 'regularity',
    rarity: 'advanced',
    icon: '🏅',
    nameEn: '50 Transactions',
    nameFr: '50 transactions',
    descriptionEn: 'Add 50 transactions',
    descriptionFr: 'Ajoutez 50 transactions',
    targetCount: 50,
  },
  {
    key: 'transactions_100',
    category: 'regularity',
    rarity: 'milestone',
    icon: '🏆',
    nameEn: '100 Transactions',
    nameFr: '100 transactions',
    descriptionEn: 'Add 100 transactions',
    descriptionFr: 'Ajoutez 100 transactions',
    targetCount: 100,
  },
  {
    key: 'recurring_3',
    category: 'regularity',
    rarity: 'important',
    icon: '⚙️',
    nameEn: '3 Recurring Active',
    nameFr: '3 récurrences actives',
    descriptionEn: 'Have 3 active recurring transactions',
    descriptionFr: 'Ayez 3 transactions récurrentes actives',
    targetCount: 3,
  },
  {
    key: 'goals_5',
    category: 'regularity',
    rarity: 'advanced',
    icon: '🌟',
    nameEn: '5 Goals Created',
    nameFr: '5 objectifs créés',
    descriptionEn: 'Create 5 savings goals',
    descriptionFr: "Créez 5 objectifs d'épargne",
    targetCount: 5,
  },

  // ── Category 4: Financial Control ──────────────────────────────
  {
    key: 'categorized_expenses',
    category: 'financial_control',
    rarity: 'important',
    icon: '🗂️',
    nameEn: 'Categorized Expenses',
    nameFr: 'Dépenses catégorisées',
    descriptionEn: 'Have at least 10 categorized expenses',
    descriptionFr: 'Ayez au moins 10 dépenses catégorisées',
    targetCount: 10,
  },
  {
    key: 'clear_vision',
    category: 'financial_control',
    rarity: 'advanced',
    icon: '🔮',
    nameEn: 'Clear Vision',
    nameFr: 'Vision claire',
    descriptionEn: 'Have enough data for a useful financial overview',
    descriptionFr: 'Renseignez suffisamment de données pour une vue exploitable',
  },
  {
    key: 'organized_accounts',
    category: 'financial_control',
    rarity: 'important',
    icon: '📁',
    nameEn: 'Organized Accounts',
    nameFr: 'Comptes organisés',
    descriptionEn: 'Have at least 3 accounts set up',
    descriptionFr: 'Ayez au moins 3 comptes configurés',
    targetCount: 3,
  },

  // ── Category 5: Product Exploration ────────────────────────────
  {
    key: 'explore_calendar',
    category: 'product_exploration',
    rarity: 'basic',
    icon: '📅',
    nameEn: 'Calendar Discovery',
    nameFr: 'Découverte du calendrier',
    descriptionEn: 'Visit the calendar view for the first time',
    descriptionFr: 'Visitez la vue calendrier pour la première fois',
  },
  {
    key: 'explore_account_detail',
    category: 'product_exploration',
    rarity: 'basic',
    icon: '🔎',
    nameEn: 'Account Detail',
    nameFr: 'Détail de compte',
    descriptionEn: 'View an account detail page',
    descriptionFr: "Consultez le détail d'un compte",
  },
  {
    key: 'explore_transactions',
    category: 'product_exploration',
    rarity: 'basic',
    icon: '📜',
    nameEn: 'Transaction History',
    nameFr: 'Historique consulté',
    descriptionEn: 'Browse the transactions list',
    descriptionFr: 'Explorez la liste de transactions',
  },
  {
    key: 'explore_settings',
    category: 'product_exploration',
    rarity: 'basic',
    icon: '⚙️',
    nameEn: 'Settings Explorer',
    nameFr: 'Paramètres explorés',
    descriptionEn: 'Visit the financial settings',
    descriptionFr: 'Visitez les paramètres financiers',
  },
  {
    key: 'explore_achievements',
    category: 'product_exploration',
    rarity: 'basic',
    icon: '🏆',
    nameEn: 'Achievement Hunter',
    nameFr: 'Succès consultés',
    descriptionEn: 'Open the achievements center for the first time',
    descriptionFr: 'Ouvrez le centre de succès pour la première fois',
  },
];

// Helper: get achievement definition by key
export function getAchievementByKey(key: string): AchievementDefinition | undefined {
  return ACHIEVEMENTS.find((a) => a.key === key);
}

// Helper: get achievements by category
export function getAchievementsByCategory(category: AchievementCategory): AchievementDefinition[] {
  return ACHIEVEMENTS.filter((a) => a.category === category);
}

// Rarity display config
export const RARITY_CONFIG: Record<
  AchievementRarity,
  { labelEn: string; labelFr: string; color: string; bgColor: string }
> = {
  basic: {
    labelEn: 'Basic',
    labelFr: 'Basique',
    color: 'text-gray-600 dark:text-gray-400',
    bgColor: 'bg-gray-100 dark:bg-gray-700/50',
  },
  important: {
    labelEn: 'Important',
    labelFr: 'Important',
    color: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-50 dark:bg-blue-900/30',
  },
  advanced: {
    labelEn: 'Advanced',
    labelFr: 'Avancé',
    color: 'text-purple-600 dark:text-purple-400',
    bgColor: 'bg-purple-50 dark:bg-purple-900/30',
  },
  milestone: {
    labelEn: 'Milestone',
    labelFr: 'Palier clé',
    color: 'text-amber-600 dark:text-amber-400',
    bgColor: 'bg-amber-50 dark:bg-amber-900/30',
  },
};
